import type { RadioButtonAssignments } from '../../services/admin';
import { DEFAULT_ASSIGNMENT, actionOptions, type AssignmentChoices } from './buttonAssignments';

interface Props {
  data: RadioButtonAssignments | null;
  choices: AssignmentChoices;
  onChange: (choices: AssignmentChoices) => void;
}

// One dropdown per user-changeable button of the radio's canonical model. Fixed buttons (e.g.
// PTT) are never listed by the server, so they cannot be reassigned here.
export default function ButtonAssignmentsEditor({ data, choices, onChange }: Props) {
  if (!data) return <p className="text-xs text-gray-500 pt-3">Loading button assignments…</p>;
  if (data.buttons.length === 0) {
    return (
      <p className="text-xs text-gray-500 pt-3" data-no-assignable-buttons>
        This radio's model has no user-changeable buttons (Settings &gt; Model Catalog).
      </p>
    );
  }
  return (
    <div className="space-y-2 pt-3">
      <p className="text-xs text-gray-500">
        User-changeable buttons of this radio's model. "Default" keeps the button's built-in behaviour. Only actions the model supports are offered.
      </p>
      {data.buttons.map((b) => (
        <label key={b.button_function_id} className="flex items-center gap-3" data-button-assignment={b.name}>
          <span className="w-32 shrink-0 text-sm font-semibold text-gray-300 font-mono">{b.name}</span>
          <select
            value={choices[b.button_function_id] ?? DEFAULT_ASSIGNMENT}
            onChange={(e) => onChange({ ...choices, [b.button_function_id]: e.target.value })}
            className="flex-1 px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
          >
            <option value={DEFAULT_ASSIGNMENT}>Default (built-in behaviour)</option>
            {actionOptions(b.allowed_actions, data.actions).map((a) => (
              <option key={a.code} value={a.code}>{a.name}</option>
            ))}
          </select>
        </label>
      ))}
    </div>
  );
}
